import { DECK, SUITS } from "./engine.js";
import { scopedDatabaseName } from "./runtime-context.js";
import { date, number, suitName, t } from "./i18n.js";
import { decorateAnalysis, loadModel, saveModel, trainModel } from "./learning.js";
import {
  createDealState,
  finalizeDealState,
  mergeVisibleObservation,
  shouldRotateDeal,
} from "./belote-journal.js";
import {
  averageHash,
  captureRegionHashes,
  changedCaptureRegions,
  classifyFrame,
  createRecordId,
  hashDifference,
  safeInputSnapshot,
  serializeAnalysis,
  shouldPersistTimelineFrame,
  stripTemporaryCapture,
} from "./observer-core.js";

const DB_NAME = scopedDatabaseName("belote-observer-local");
const STORE_NAME = "records";
const DB_VERSION = 2;

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(STORE_NAME)
        ? request.transaction.objectStore(STORE_NAME)
        : database.createObjectStore(STORE_NAME, { keyPath: "id" });
      if (!store.indexNames.contains("capturedAt")) store.createIndex("capturedAt", "capturedAt");
      if (!store.indexNames.contains("sessionId")) store.createIndex("sessionId", "sessionId");
      if (!store.indexNames.contains("gameId")) store.createIndex("gameId", "gameId");
      if (!store.indexNames.contains("kind")) store.createIndex("kind", "kind");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore(mode, callback) {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, mode);
    const completion = new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
    const result = await callback(transaction.objectStore(STORE_NAME));
    await completion;
    return result;
  } finally {
    database.close();
  }
}

async function listRecords() {
  const records = await withStore("readonly", (store) => requestResult(store.getAll()));
  return records.sort((first, second) => second.capturedAt.localeCompare(first.capturedAt));
}

async function saveRecord(record) {
  await withStore("readwrite", (store) => requestResult(store.put(record)));
  return record;
}

async function removeRecord(id) {
  await withStore("readwrite", (store) => requestResult(store.delete(id)));
}

async function clearRecords() {
  await withStore("readwrite", (store) => requestResult(store.clear()));
}

function canvasBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.72));
}

function suitInfo(suitId) {
  const suit = SUITS.find((item) => item.id === suitId);
  return suit ? { ...suit, name: suitName(suit.id) } : null;
}

function cardLabel(cardId) {
  const card = DECK.find((item) => item.id === cardId);
  if (!card) return cardId || t("common.none");
  return `${card.rank}${suitInfo(card.suit).symbol}`;
}

function phaseLabel(phase) {
  return t(`observer.phase.${phase === "dice" ? "table" : phase}`);
}

function decisionOptions(selected = "") {
  const options = [
    ["", t("observer.decision.placeholder")],
    ["pass", t("observer.decision.pass")],
    ...SUITS.map((suit) => [`take:${suit.id}`, t("observer.decision.take", { symbol: suit.symbol })]),
    ...DECK.map((card) => [`play:${card.id}`, t("observer.decision.play", { card: cardLabel(card.id) })]),
  ];
  return options
    .map(([value, label]) => `<option value="${value}" ${selected === value ? "selected" : ""}>${label}</option>`)
    .join("");
}

function outcomeOptions(selected = "") {
  return [
    ["", t("observer.outcome.placeholder")],
    ["win", t("observer.outcome.win")],
    ["loss", t("observer.outcome.loss")],
  ]
    .map(([value, label]) => `<option value="${value}" ${selected === value ? "selected" : ""}>${label}</option>`)
    .join("");
}

function downloadJson(payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `belote-apprentissage-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function createObserver({ stateProvider, onModelChanged } = {}) {
  const elements = {
    launch: document.querySelector("#observer-launch"),
    dialog: document.querySelector("#observer-dialog"),
    close: document.querySelector("#observer-close"),
    startScreen: document.querySelector("#observer-start-screen"),
    startCamera: document.querySelector("#observer-start-camera"),
    stop: document.querySelector("#observer-stop"),
    capture: document.querySelector("#observer-capture"),
    video: document.querySelector("#observer-video"),
    previewEmpty: document.querySelector("#observer-preview-empty"),
    status: document.querySelector("#observer-status"),
    phase: document.querySelector("#observer-phase"),
    autoCapture: document.querySelector("#observer-auto-capture"),
    stats: document.querySelector("#observer-stats"),
    model: document.querySelector("#observer-model"),
    list: document.querySelector("#observer-list"),
    export: document.querySelector("#observer-export"),
    clear: document.querySelector("#observer-clear"),
  };

  let stream = null;
  let timer = null;
  let sourceKind = null;
  let lastHash = null;
  let lastSavedAt = 0;
  let currentPhase = "unknown";
  let records = [];
  let objectUrls = [];
  let model = loadModel();
  let lastAnalysisSignature = "";
  let lastRegionHashes = null;
  let recordingSession = null;
  let activeGame = null;
  let activeDeal = null;
  let captureSequence = 0;
  let gameSequence = 0;
  let dealSequence = 0;
  let stablePhase = "unknown";
  let stablePhaseFrames = 0;
  let renderTimer = null;
  let finalizingGame = false;
  let sampling = false;

  const sampleCanvas = document.createElement("canvas");
  sampleCanvas.width = 80;
  sampleCanvas.height = 90;
  const frameCanvas = document.createElement("canvas");

  function setStatus(text, tone = "idle") {
    elements.status.textContent = text;
    elements.status.dataset.tone = tone;
  }

  function getCurrentState() {
    return stateProvider?.() || {};
  }

  async function retrain() {
    model = trainModel(records);
    saveModel(model);
    onModelChanged?.(model);
  }

  function renderModel() {
    const readiness = Math.min(100, Math.round((model.outcomeSamples / 20) * 100));
    const favorite = Object.entries(model.style.suits).sort((a, b) => b[1] - a[1])[0];
    elements.model.innerHTML = `
      <div class="model-heading"><span>${t("observer.profile.heading")}</span><strong>${t("observer.profile.results", { count: model.outcomeSamples })}</strong></div>
      <div class="model-track"><span style="width:${readiness}%"></span></div>
      <p>${model.outcomeSamples < 5
        ? t("observer.profile.pending")
        : t("observer.profile.active", { count: model.decisionSamples, favorite: favorite?.[1] ? t("observer.profile.favorite", { symbol: suitInfo(favorite[0]).symbol }) : "" })}</p>
      <p class="model-convention">${t("observer.profile.conventions", { seconds: model.preferences.forcedMoveDelayMs / 1000 })}</p>`;
  }

  function recordSummary(record) {
    const hand = record.input?.hand?.map(cardLabel).join(" ") || t("observer.summary.noHand");
    const center = record.input?.center ? t("observer.summary.upcard", { card: cardLabel(record.input.center) }) : "";
    const position = record.input?.position ? t("observer.summary.position", { position: record.input.position }) : "";
    return `${hand} ${center} ${position}`.trim();
  }

  function renderRecords() {
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls = [];
    const imageRecords = records.filter((record) => record.image instanceof Blob).length;
    const annotated = records.filter((record) => record.decision).length;
    const bytes = records.reduce((total, record) => total + (record.image?.size || 0), 0);
    const sessions = new Set(records.map((record) => record.sessionId).filter(Boolean)).size;
    const games = records.filter((record) => record.kind === "game-end").length;
    const deals = records.filter((record) => record.kind === "deal-end").length;
    elements.stats.textContent = t("observer.stats", { sessions, games, deals, events: records.length, annotated, images: imageRecords, megabytes: number(bytes / 1048576, { maximumFractionDigits: 1 }) });

    if (!records.length) {
      elements.list.innerHTML = `<div class="observer-empty-list">${t("observer.empty")}</div>`;
      renderModel();
      return;
    }

    elements.list.innerHTML = records.slice(0, 12).map((record) => {
      let thumbnail = '<span class="record-placeholder">♣</span>';
      if (record.image instanceof Blob) {
        const url = URL.createObjectURL(record.image);
        objectUrls.push(url);
        thumbnail = `<img src="${url}" alt="${t("observer.captureAlt")}" />`;
      }
      return `
        <article class="observer-record" data-record="${record.id}">
          <div class="record-thumb">${thumbnail}</div>
          <div class="record-body">
            <div class="record-title"><strong>${phaseLabel(record.phase)}</strong><time>${date(record.capturedAt, { hour: "2-digit", minute: "2-digit" })}</time></div>
            <p>${recordSummary(record)}</p>
            <div class="record-fields">
              <select data-record-field="decision" aria-label="${t("observer.decisionAria")}">${decisionOptions(record.decision)}</select>
              <select data-record-field="outcome" aria-label="${t("observer.outcomeAria")}">${outcomeOptions(record.outcome)}</select>
            </div>
          </div>
          <div class="record-actions">
            <button type="button" data-link-current title="${t("observer.linkTitle")}">${t("observer.link")}</button>
            <button type="button" data-delete-record title="${t("observer.deleteTitle")}">×</button>
          </div>
        </article>`;
    }).join("");
    renderModel();
  }

  async function refreshRecords() {
    records = await listRecords();
    if (!recordingSession) {
      const interrupted = records
        .filter((record) => record.imageTemporary && record.image instanceof Blob)
        .map((record) => stripTemporaryCapture(record));
      if (interrupted.length) {
        await withStore("readwrite", (store) => Promise.all(interrupted.map((record) => requestResult(store.put(record)))));
        const byId = new Map(interrupted.map((record) => [record.id, record]));
        records = records.map((record) => byId.get(record.id) || record);
      }
    }
    await retrain();
    renderRecords();
  }

  async function store(record) {
    await saveRecord(record);
    records = [record, ...records.filter((item) => item.id !== record.id)];
    await retrain();
    renderRecords();
  }

  function scheduleRender() {
    if (renderTimer) return;
    renderTimer = setTimeout(() => {
      renderTimer = null;
      renderRecords();
    }, 350);
  }

  async function appendTimelineRecord(record) {
    await saveRecord(record);
    records = [record, ...records.filter((item) => item.id !== record.id)];
    scheduleRender();
    return record;
  }

  async function beginRecordingSession(kind) {
    const startedAt = new Date().toISOString();
    recordingSession = {
      id: createRecordId("session"),
      source: kind,
      startedAt,
      startedMs: Date.now(),
    };
    captureSequence = 0;
    gameSequence = 0;
    dealSequence = 0;
    await appendTimelineRecord({
      id: createRecordId("event"),
      kind: "session-start",
      phase: "session-start",
      capturedAt: startedAt,
      sessionId: recordingSession.id,
      gameId: null,
      sequence: captureSequence,
      source: kind,
      image: null,
      data: { exhaustive: true, requestedFramesPerSecond: kind === "screen" ? 12 : 8 },
    });
  }

  async function beginDetectedGame(phase) {
    if (!recordingSession || activeGame) return;
    gameSequence += 1;
    activeGame = {
      id: createRecordId("game"),
      number: gameSequence,
      startedAt: new Date().toISOString(),
      startedMs: Date.now(),
      eventCount: 0,
      phases: new Set([phase]),
    };
    await appendTimelineRecord({
      id: createRecordId("event"),
      kind: "game-start",
      phase: "game-start",
      capturedAt: activeGame.startedAt,
      sessionId: recordingSession.id,
      gameId: activeGame.id,
      sequence: ++captureSequence,
      source: sourceKind,
      image: null,
      data: { gameNumber: activeGame.number, detectedFrom: phase },
    });
  }

  async function beginDetectedDeal(phase) {
    if (!recordingSession || !activeGame || activeDeal) return;
    dealSequence += 1;
    activeDeal = createDealState({
      id: createRecordId("deal"),
      number: dealSequence,
      startedAt: new Date().toISOString(),
      detectedFrom: phase,
    });
    activeDeal.hadPlay = phase === "play";
    await appendTimelineRecord({
      id: createRecordId("event"),
      kind: "deal-start",
      phase: "deal-start",
      capturedAt: activeDeal.startedAt,
      sessionId: recordingSession.id,
      gameId: activeGame.id,
      dealId: activeDeal.id,
      sequence: ++captureSequence,
      source: sourceKind,
      image: null,
      data: { dealNumber: activeDeal.number, detectedFrom: phase },
    });
  }

  async function finishDetectedDeal(reason = "deal-ended") {
    if (!recordingSession || !activeGame || !activeDeal) return;
    const deal = finalizeDealState(activeDeal, { reason });
    await appendTimelineRecord({
      id: createRecordId("event"),
      kind: "deal-end",
      phase: "deal-end",
      capturedAt: deal.endedAt,
      sessionId: recordingSession.id,
      gameId: activeGame.id,
      dealId: deal.id,
      sequence: ++captureSequence,
      source: sourceKind,
      image: null,
      data: { deal },
    });
    activeGame.deals = (activeGame.deals || 0) + 1;
    activeDeal = null;
  }

  async function ingestVisibleObservation(observation = {}) {
    if (!activeDeal) return false;
    activeDeal = mergeVisibleObservation(activeDeal, observation);
    await appendTimelineRecord({
      id: createRecordId("state"),
      kind: "visible-state",
      phase: "visible-state",
      capturedAt: observation.capturedAt || new Date().toISOString(),
      sessionId: recordingSession?.id || null,
      gameId: activeGame?.id || null,
      dealId: activeDeal.id,
      sequence: ++captureSequence,
      source: observation.source || "vision",
      image: null,
      data: { observation },
    });
    return true;
  }

  async function purgeGameCaptures(gameId) {
    const purgedAt = new Date().toISOString();
    const replacements = records
      .filter((record) => record.gameId === gameId && record.image instanceof Blob)
      .map((record) => stripTemporaryCapture(record, purgedAt));
    if (!replacements.length) return 0;
    await withStore("readwrite", (store) => Promise.all(replacements.map((record) => requestResult(store.put(record)))));
    const replacementById = new Map(replacements.map((record) => [record.id, record]));
    records = records.map((record) => replacementById.get(record.id) || record);
    scheduleRender();
    return replacements.length;
  }

  async function finishDetectedGame(reason = "result-detected") {
    if (!recordingSession || !activeGame || finalizingGame) return;
    finalizingGame = true;
    const game = activeGame;
    try {
      await finishDetectedDeal(reason);
      await appendTimelineRecord({
        id: createRecordId("event"),
        kind: "game-end",
        phase: "game-end",
        capturedAt: new Date().toISOString(),
        sessionId: recordingSession.id,
        gameId: game.id,
        sequence: ++captureSequence,
        source: sourceKind,
        image: null,
        data: {
          gameNumber: game.number,
          reason,
          durationMs: Math.max(0, Date.now() - game.startedMs),
          eventCount: game.eventCount,
          deals: game.deals || 0,
          phases: [...game.phases],
        },
      });
      const purged = await purgeGameCaptures(game.id);
      setStatus(t("observer.status.gameSaved", { game: game.number, count: purged }), "success");
      activeGame = null;
    } finally {
      finalizingGame = false;
    }
  }

  async function finishRecordingSession(reason = "capture-stopped") {
    if (!recordingSession) return;
    await finishDetectedGame(reason);
    const session = recordingSession;
    await appendTimelineRecord({
      id: createRecordId("event"),
      kind: "session-end",
      phase: "session-end",
      capturedAt: new Date().toISOString(),
      sessionId: session.id,
      gameId: null,
      sequence: ++captureSequence,
      source: session.source,
      image: null,
      data: { reason, durationMs: Math.max(0, Date.now() - session.startedMs), games: gameSequence },
    });
    recordingSession = null;
  }

  function drawCurrentFrame() {
    const videoWidth = elements.video.videoWidth;
    const videoHeight = elements.video.videoHeight;
    if (!videoWidth || !videoHeight) return null;
    const maxWidth = 760;
    const scale = Math.min(1, maxWidth / videoWidth);
    frameCanvas.width = Math.round(videoWidth * scale);
    frameCanvas.height = Math.round(videoHeight * scale);
    const context = frameCanvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(elements.video, 0, 0, frameCanvas.width, frameCanvas.height);
    return context;
  }

  async function sampleFrame(force = false) {
    if (!stream || elements.video.readyState < 2 || sampling) return;
    sampling = true;
    try {
      const frameContext = drawCurrentFrame();
      if (!frameContext) return;

      const cropCanvas = document.createElement("canvas");
      const screenCrop = sourceKind === "screen";
      const sourceX = screenCrop ? Math.round(frameCanvas.width * 0.01) : 0;
      const sourceY = screenCrop ? Math.round(frameCanvas.height * 0.1) : 0;
      const sourceWidth = screenCrop ? Math.round(frameCanvas.width * 0.95) : frameCanvas.width;
      const sourceHeight = screenCrop ? Math.round(frameCanvas.height * 0.88) : frameCanvas.height;
      cropCanvas.width = Math.min(700, sourceWidth);
      cropCanvas.height = Math.round(sourceHeight * (cropCanvas.width / sourceWidth));
      const cropContext = cropCanvas.getContext("2d", { willReadFrequently: true });
      cropContext.drawImage(
        frameCanvas,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        cropCanvas.width,
        cropCanvas.height,
      );

      const sampleContext = sampleCanvas.getContext("2d", { willReadFrequently: true });
      sampleContext.drawImage(cropCanvas, 0, 0, sampleCanvas.width, sampleCanvas.height);
      const sample = sampleContext.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height);
      const classification = classifyFrame(sample);
      const croppedFrame = cropContext.getImageData(0, 0, cropCanvas.width, cropCanvas.height);
      const detectedPhase = classification.phase;
      const previousPhase = currentPhase;
      if (detectedPhase === stablePhase) stablePhaseFrames += 1;
      else {
        stablePhase = detectedPhase;
        stablePhaseFrames = 1;
      }
      if (stablePhaseFrames >= 2 || currentPhase === "unknown") currentPhase = stablePhase;
      elements.phase.textContent = phaseLabel(currentPhase);
      elements.phase.dataset.phase = currentPhase;

      if (!activeGame && ["bidding", "play"].includes(currentPhase) && stablePhaseFrames >= 2) {
        await beginDetectedGame(currentPhase);
      }
      if (activeGame && shouldRotateDeal({ activeDeal, previousPhase, currentPhase, stablePhaseFrames })) {
        await finishDetectedDeal("next-bidding");
      }
      if (activeGame && !activeDeal && ["bidding", "play"].includes(currentPhase) && stablePhaseFrames >= 2) {
        await beginDetectedDeal(currentPhase);
      }
      if (activeDeal && currentPhase === "play") activeDeal.hadPlay = true;
      activeGame?.phases.add(currentPhase);

      const hash = averageHash(sample);
      const difference = hashDifference(lastHash, hash);
      const regionHashes = captureRegionHashes(croppedFrame);
      const changedRegions = changedCaptureRegions(lastRegionHashes, regionHashes, 0.025);
      const now = Date.now();
      const shouldSave = shouldPersistTimelineFrame({
        force,
        enabled: elements.autoCapture.checked && Boolean(activeGame),
        phase: currentPhase,
        changedRegions,
        now,
        lastSavedAt,
        minIntervalMs: sourceKind === "screen" ? 90 : 130,
        heartbeatMs: 1800,
      });
      lastHash = hash;
      lastRegionHashes = regionHashes;

      if (shouldSave && recordingSession) {
        const image = await canvasBlob(cropCanvas);
        const current = getCurrentState();
        const record = {
          id: createRecordId("frame"),
          kind: "timeline-frame",
          capturedAt: new Date(now).toISOString(),
          phase: currentPhase,
          source: sourceKind,
          sessionId: recordingSession.id,
          gameId: activeGame?.id || null,
          dealId: activeDeal?.id || null,
          dealNumber: activeDeal?.number || null,
          sequence: ++captureSequence,
          offsetMs: Math.max(0, now - recordingSession.startedMs),
          image,
          imageTemporary: true,
          input: safeInputSnapshot(current),
          analysis: serializeAnalysis(current.lastAnalysis),
          decision: "",
          outcome: "",
          visual: {
            difference,
            ratios: classification.ratios,
            changedRegions,
            regionHashes,
            frame: { width: cropCanvas.width, height: cropCanvas.height },
          },
        };
        if (activeGame) activeGame.eventCount += 1;
        lastSavedAt = now;
        await appendTimelineRecord(record);
        setStatus(t("observer.status.gameProgress", { game: activeGame?.number || "", count: activeGame?.eventCount || 0 }), "live");
      }

      if (activeGame && currentPhase === "result" && stablePhaseFrames >= 6) {
        await finishDetectedDeal("result-detected");
      }
      if (activeGame && currentPhase === "lobby" && stablePhaseFrames >= 6) {
        await finishDetectedGame("lobby-returned");
      }
    } finally {
      sampling = false;
    }
  }

  async function stopSource(reason = "capture-stopped") {
    if (timer) clearInterval(timer);
    timer = null;
    const previousStream = stream;
    stream = null;
    previousStream?.getTracks().forEach((track) => track.stop());
    await finishRecordingSession(reason);
    elements.video.srcObject = null;
    elements.video.hidden = true;
    elements.previewEmpty.hidden = false;
    elements.stop.hidden = true;
    elements.capture.disabled = true;
    elements.startScreen.disabled = false;
    elements.startCamera.disabled = false;
    elements.phase.textContent = t("observer.waiting");
    setStatus(t("observer.status.stopped"));
  }

  async function startSource(kind) {
    await stopSource("source-replaced");
    try {
      setStatus(t("observer.status.permission"), "working");
      stream = kind === "screen"
        ? await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 8, max: 12 } }, audio: false })
        : await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, frameRate: { ideal: 8, max: 12 } }, audio: false });
      sourceKind = kind;
      elements.video.srcObject = stream;
      await elements.video.play();
      elements.video.hidden = false;
      elements.previewEmpty.hidden = true;
      elements.stop.hidden = false;
      elements.capture.disabled = false;
      elements.startScreen.disabled = true;
      elements.startCamera.disabled = true;
      lastHash = null;
      lastRegionHashes = null;
      lastSavedAt = 0;
      stablePhase = "unknown";
      stablePhaseFrames = 0;
      currentPhase = "unknown";
      await beginRecordingSession(kind);
      stream.getVideoTracks()[0]?.addEventListener("ended", () => void stopSource("share-ended"), { once: true });
      timer = setInterval(() => void sampleFrame(false), kind === "screen" ? 85 : 125);
      setStatus(t(kind === "screen" ? "observer.status.screen" : "observer.status.camera"), "live");
    } catch (error) {
      await stopSource("capture-error");
      setStatus(t(error?.name === "NotAllowedError" ? "observer.status.denied" : "observer.status.unavailable"), "error");
    }
  }

  async function recordAnalysis(input, analysis) {
    const snapshot = safeInputSnapshot(input);
    const serialized = serializeAnalysis(analysis);
    const signature = JSON.stringify({ snapshot, bySuit: serialized?.bySuit });
    if (signature === lastAnalysisSignature) return;
    lastAnalysisSignature = signature;
    await store({
      id: createRecordId("analysis"),
      capturedAt: new Date().toISOString(),
      phase: "analysis",
      source: "calculator",
      input: snapshot,
      analysis: serialized,
      decision: "",
      outcome: "",
      image: null,
    });
  }

  async function recordPracticeDeal(deal) {
    await store({
      id: createRecordId("practice"),
      kind: "practice-deal",
      capturedAt: new Date().toISOString(),
      phase: "practice-result",
      source: "practice-engine",
      sessionId: null,
      gameId: deal.id,
      dealId: deal.id,
      input: {
        hand: deal.initialHands?.[0]?.slice(0, 5) || [],
        center: deal.center || null,
        position: null,
      },
      analysis: null,
      decision: deal.bids?.find((bid) => bid.seat === 0 && bid.action === "take")?.suit
        ? `take:${deal.bids.find((bid) => bid.seat === 0 && bid.action === "take").suit}`
        : "",
      outcome: deal.result?.userOutcome || "",
      image: null,
      data: { deal },
    });
  }

  function decorate(analysis, input) {
    return decorateAnalysis(analysis, safeInputSnapshot(input), model);
  }

  elements.launch?.addEventListener("click", () => {
    if (typeof elements.dialog.showModal === "function") elements.dialog.showModal();
    else elements.dialog.setAttribute("open", "");
    void refreshRecords();
  });
  elements.close?.addEventListener("click", () => elements.dialog.close());
  elements.dialog?.addEventListener("click", (event) => {
    if (event.target === elements.dialog) elements.dialog.close();
  });
  elements.startScreen?.addEventListener("click", () => void startSource("screen"));
  elements.startCamera?.addEventListener("click", () => void startSource("camera"));
  elements.stop?.addEventListener("click", () => void stopSource("manual-stop"));
  elements.capture?.addEventListener("click", () => void sampleFrame(true));
  elements.list?.addEventListener("change", async (event) => {
    const field = event.target.dataset.recordField;
    const container = event.target.closest("[data-record]");
    if (!field || !container) return;
    const record = records.find((item) => item.id === container.dataset.record);
    if (!record) return;
    record[field] = event.target.value;
    await store(record);
  });
  elements.list?.addEventListener("click", async (event) => {
    const container = event.target.closest("[data-record]");
    if (!container) return;
    const record = records.find((item) => item.id === container.dataset.record);
    if (!record) return;
    if (event.target.closest("[data-delete-record]")) {
      await removeRecord(record.id);
      records = records.filter((item) => item.id !== record.id);
      await retrain();
      renderRecords();
    }
    if (event.target.closest("[data-link-current]")) {
      const current = getCurrentState();
      record.input = safeInputSnapshot(current);
      record.analysis = serializeAnalysis(current.lastAnalysis) || record.analysis;
      await store(record);
    }
  });
  elements.export?.addEventListener("click", async () => {
    setStatus(t("observer.status.exporting"), "working");
    const exported = records.map((record) => ({
      ...record,
      image: null,
      imageTemporary: false,
    }));
    downloadJson({
      version: 2,
      exportedAt: new Date().toISOString(),
      mediaPolicy: "captures-temporaires-supprimees",
      model,
      records: exported,
    });
    setStatus(t("observer.status.exported"), "success");
  });
  elements.clear?.addEventListener("click", async () => {
    if (!window.confirm(t("observer.status.clearConfirm"))) return;
    await clearRecords();
    records = [];
    await retrain();
    renderRecords();
    setStatus(t("observer.status.cleared"));
  });

  void refreshRecords().catch(() => setStatus(t("observer.status.storage"), "error"));

  window.addEventListener("belote-visible-state", (event) => {
    void ingestVisibleObservation(event.detail).catch(() => setStatus(t("observer.status.invalidState"), "error"));
  });

  return {
    decorateAnalysis: decorate,
    getModel: () => model,
    recordAnalysis,
    recordPracticeDeal,
    ingestVisibleObservation,
    stop: () => void stopSource("observer-stop"),
  };
}
